import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { HiStar } from 'react-icons/hi2';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import {
  useMultiGate,
  useMultiGateProgress,
  useMultiGateReferenceSessions,
  useSetMultiGateGates,
  useSetMultiGateReference,
  useSyncMultiGate,
} from '../../hooks/useSessions';
import { useRoutesForManagement } from '../../hooks/useRoutes';
import { useStoredState } from '../../hooks/useStoredState';
import {
  buildGateOptions, comparisonGateIds, GateOption, SyncProgressView, syncProgressView,
} from '../../utils/multiGate';

function formatSynced(ts: string | null): string {
  if (!ts) return 'never synced';
  return `synced ${new Date(ts).toLocaleString()}`;
}

/**
 * Advanced → Multi-gate.
 *
 * Pick a reference session, then tick the gates to compare it on: the reference's own gate plus any
 * starred gate in the team. Each ticked gate gets a comparison session that mirrors the reference
 * (configs, config values, copies of the runs) with that gate. An edit to the reference queues a sync by
 * itself (multigate_service.queue_sync_after_edit). Synchronise re-mirrors the reference onto every
 * comparison, repairing stray or duplicate copies on the way, and reprocesses every comparison run; a
 * progress bar follows both parts. Calibration always comes from the reference.
 * Rebuild empties the comparisons and mirrors them from nothing. It is a temporary fallback, to be removed
 * once Synchronise's repairs are proven on production (tasks/pending-verification.md).
 * "Set as Reference" swaps a comparison's gate onto the reference session, which re-calibrates on it.
 */
export default function MultiGate(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedId, setStoredId] = useStoredState<string>('multiGate_selectedSessionId', '');
  const selectedId = searchParams.get('session') ?? storedId;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmReferenceGate, setConfirmReferenceGate] = useState<string | null>(null);
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const queryClient = useQueryClient();

  const { data: referenceSessions, isPending: isSessionsPending } = useMultiGateReferenceSessions();
  const { data: group, isFetching: isGroupFetching, isError: isGroupError } = useMultiGate(selectedId);
  const { data: routes, isPending: isRoutesPending } = useRoutesForManagement();
  const setGates = useSetMultiGateGates();
  const syncGroup = useSyncMultiGate();
  const setReference = useSetMultiGateReference();
  const { data: progress } = useMultiGateProgress(group?.reference.id ?? '');
  const progressView = syncProgressView(progress);
  const isSyncActive = progress?.active ?? false;

  // When a sync finishes, refresh the run counts and "synced" times.
  const wasSyncActive = useRef(false);
  useEffect(() => {
    if (wasSyncActive.current && !isSyncActive) {
      queryClient.invalidateQueries({ queryKey: ['session-multigate'] });
      const failed = progress?.runs.failed ?? 0;
      if (failed > 0) toast.error(`Synchronise finished; ${failed} comparison runs failed to process`);
      else toast.success('Synchronise finished');
    }
    wasSyncActive.current = isSyncActive;
  }, [isSyncActive]);

  const selectSession = (sessionId: string) => {
    setStoredId(sessionId);
    setSearchParams(sessionId ? { session: sessionId } : {}, { replace: true });
  };

  // Opened on a comparison session: switch to its reference.
  useEffect(() => {
    if (group && group.reference.id !== selectedId) selectSession(group.reference.id);
  }, [group]);

  const savedGateIds = useMemo(() => (group ? comparisonGateIds(group) : []), [group]);
  useEffect(() => {
    setChecked(new Set(savedGateIds));
    setConfirmRemove(false);
    setConfirmReferenceGate(null);
    setConfirmRebuild(false);
  }, [savedGateIds]);

  const options: GateOption[] = useMemo(
    () => (group ? buildGateOptions(routes ?? [], group) : []),
    [routes, group],
  );

  const toAdd = [...checked].filter((id) => !savedGateIds.includes(id));
  const toRemove = savedGateIds.filter((id) => !checked.has(id));
  const isDirty = toAdd.length > 0 || toRemove.length > 0;

  const toggle = (gateId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(gateId)) next.delete(gateId);
      else next.add(gateId);
      return next;
    });
    setConfirmRemove(false);
  };

  const save = async () => {
    if (!group) return;
    if (toRemove.length > 0 && !confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    try {
      const result = await setGates.mutateAsync({ sessionId: group.reference.id, gatesIds: [...checked] });
      const added = result.added?.length ?? 0;
      const removed = result.removed?.length ?? 0;
      toast.success(`Comparison sessions: ${added} added, ${removed} removed`);
    } catch {
      toast.error('Failed to update the comparison gates');
    }
  };

  // First click arms the row, second click swaps.
  const makeReference = async (gateId: string) => {
    if (!group) return;
    if (confirmReferenceGate !== gateId) {
      setConfirmReferenceGate(gateId);
      return;
    }
    try {
      const result = await setReference.mutateAsync({ sessionId: group.reference.id, gatesId: gateId });
      toast.success(`Reference gate changed — ${result.reprocess_queued ?? 0} reference runs reprocessing; `
        + 'comparisons re-sync once autocal finishes');
    } catch {
      toast.error('Failed to change the reference gate');
    }
    setConfirmReferenceGate(null);
  };

  const synchronise = async (rebuild: boolean) => {
    if (!group) return;
    // Rebuild deletes and recreates every copy, so the first click only arms it.
    if (rebuild && !confirmRebuild) {
      setConfirmRebuild(true);
      return;
    }
    setConfirmRebuild(false);
    const verb = rebuild ? 'Rebuild' : 'Synchronise';
    try {
      const result = await syncGroup.mutateAsync({ sessionId: group.reference.id, rebuild });
      if (result.status === 'already_queued') toast('A synchronise is already under way');
      else toast.success(`${verb} queued`);
    } catch {
      toast.error(`Failed to queue ${verb.toLowerCase()}`);
    }
  };

  return (
    <div className="cr-w-full cr-space-y-6 cr-p-6">
      <SectionHeader title="Multi-gate" isLoading={isGroupFetching || setGates.isPending} />

      <p className="cr-max-w-3xl cr-text-base cr-text-gray-700">
        Compare one session across several gate pairs. Each ticked gate gets a comparison session
        that mirrors the reference session: its configs, its config values and copies of its runs,
        with that gate instead. All calibration comes from the reference, fitted on the reference
        gate. The comparisons follow the reference by themselves: an edit to the reference&apos;s
        settings, configs or runs is copied across about 15 seconds later, and only the comparison runs
        it affects are reprocessed. Edits made directly on a comparison session are overwritten then,
        except lap exclusions, which belong to the session they were made in. Synchronise does the same
        on demand, reprocesses every comparison run, and repairs comparisons that have lost or gained
        runs. Set as Reference makes a comparison&apos;s gate the reference gate: the reference session
        keeps its runs, takes that gate and re-calibrates on it, and the comparisons then follow.
      </p>

      <div>
        <label htmlFor="multigate-reference" className="cr-mb-2 cr-block cr-text-base cr-font-medium cr-text-blue-900">
          Reference session
        </label>
        <select
          id="multigate-reference"
          value={selectedId}
          onChange={(e) => selectSession(e.target.value)}
          disabled={isSessionsPending}
          className={'cr-w-full cr-rounded-lg cr-border cr-border-gray-300 cr-p-3 cr-text-base cr-text-blue-900 '
            + 'focus:cr-border-blue-500 focus:cr-ring-2 focus:cr-ring-blue-500'}
        >
          <option value="">Select a reference session...</option>
          {referenceSessions?.map((session: any) => (
            <option key={session.id} value={session.id}>
              {session.date ?? ''} - {session.description}
            </option>
          ))}
        </select>
      </div>

      {selectedId && isGroupError && (
        <p className="cr-text-red-700">Could not load this session&apos;s multi-gate group.</p>
      )}

      {group && (
        <div className="cr-space-y-4">
          <div className="cr-text-base cr-text-blue-900">
            Reference:{' '}
            <Link to={`/sessions/${group.reference.id}`} className="cr-font-semibold cr-underline">
              {group.reference.description}
            </Link>
            {!group.reference.gate && (
              <span className="cr-ml-2 cr-text-amber-700">(this session has no gate yet)</span>
            )}
          </div>

          {isRoutesPending ? <Spinner isFetching /> : (
            <GateTable
              options={options}
              checked={checked}
              onToggle={toggle}
              confirmReferenceGate={confirmReferenceGate}
              referenceDisabled={isDirty || setReference.isPending || !group.reference.gate}
              onSetReference={makeReference}
            />
          )}

          {options.length <= 1 && !isRoutesPending && (
            <p className="cr-text-gray-600">
              No starred gates yet. Star gates under Advanced → Routes to offer them here.
            </p>
          )}

          {confirmReferenceGate && (
            <p className="cr-rounded-md cr-border cr-border-amber-300 cr-bg-amber-50 cr-p-3 cr-text-amber-900">
              The reference session will take this gate and its current gate moves to that comparison.
              The reference&apos;s runs reprocess and autocal re-fits its calibration on the new gate;
              the comparisons re-sync when that finishes. Press Confirm to go ahead.
            </p>
          )}

          {confirmRebuild && (
            <p className="cr-rounded-md cr-border cr-border-red-300 cr-bg-red-50 cr-p-3 cr-text-red-800">
              Rebuild deletes every comparison session&apos;s configs and copied runs and copies them again
              from the reference, so all comparison runs reprocess. The comparison sessions, their names,
              notes and gates are kept. Use it only if Synchronise does not put a comparison right. Press
              Confirm rebuild to go ahead.
            </p>
          )}

          {confirmRemove && toRemove.length > 0 && (
            <p className="cr-rounded-md cr-border cr-border-red-300 cr-bg-red-50 cr-p-3 cr-text-red-800">
              Saving deletes {toRemove.length} comparison session{toRemove.length > 1 ? 's' : ''} and
              their copied runs. The reference session is not touched. Press Save again to confirm.
            </p>
          )}

          <div className="cr-flex cr-flex-wrap cr-gap-3">
            <Button
              variant={confirmRemove ? 'danger' : 'outline'}
              size="compact"
              onClick={save}
              disabled={!isDirty || setGates.isPending}
            >
              {confirmRemove ? 'Confirm and save' : 'Save'}
            </Button>
            <Button
              variant="outline"
              size="compact"
              onClick={() => synchronise(false)}
              disabled={isDirty || group.comparisons.length === 0 || syncGroup.isPending || isSyncActive}
            >
              Synchronise
            </Button>
            <Button
              variant={confirmRebuild ? 'danger' : 'ghost'}
              size="compact"
              onClick={() => synchronise(true)}
              disabled={isDirty || group.comparisons.length === 0 || syncGroup.isPending || isSyncActive}
            >
              {confirmRebuild ? 'Confirm rebuild' : 'Rebuild from scratch'}
            </Button>
          </div>

          {progressView && <SyncProgressBar view={progressView} />}
        </div>
      )}
    </div>
  );
}

interface GateTableProps {
  options: GateOption[];
  checked: Set<string>;
  onToggle: (gateId: string) => void;
  /** The gate whose Set as Reference has been clicked once and is waiting for Confirm. */
  confirmReferenceGate: string | null;
  referenceDisabled: boolean;
  onSetReference: (gateId: string) => void;
}

function GateTable({
  options, checked, onToggle, confirmReferenceGate, referenceDisabled, onSetReference,
}: GateTableProps): ReactElement {
  return (
    <div className="cr-overflow-x-auto">
      <table className="cr-w-full cr-text-left cr-text-base">
        <thead className="cr-border-b cr-border-gray-300 cr-text-blue-900">
          <tr>
            <th className="cr-w-10 cr-p-2" aria-label="Selected" />
            <th className="cr-p-2">Gate</th>
            <th className="cr-p-2">Route</th>
            <th className="cr-p-2">Distance</th>
            <th className="cr-p-2">Comparison session</th>
          </tr>
        </thead>
        <tbody>
          {options.map((option) => (
            <tr key={option.id} className="cr-border-b cr-border-gray-100">
              <td className="cr-p-2">
                <input
                  type="checkbox"
                  aria-label={`Compare on ${option.label}`}
                  checked={option.isReference || checked.has(option.id)}
                  disabled={option.isReference}
                  onChange={() => onToggle(option.id)}
                />
              </td>
              <td className="cr-p-2">
                <span className="cr-inline-flex cr-items-center cr-gap-1">
                  {option.pinned && <HiStar className="cr-text-amber-500" aria-label="Starred" />}
                  {option.label}
                </span>
              </td>
              <td className="cr-p-2">{option.routeDescription}</td>
              <td className="cr-p-2">
                {option.routeDistance != null ? `${Math.round(option.routeDistance)} m` : '—'}
              </td>
              <td className="cr-p-2">
                {option.isReference && <span className="cr-font-semibold cr-text-blue-900">Reference</span>}
                {option.comparison && (
                  <span className="cr-flex cr-flex-wrap cr-items-center cr-gap-x-3 cr-gap-y-1">
                    <Link to={`/sessions/${option.comparison.id}`} className="cr-underline">
                      {option.comparison.run_count} runs
                    </Link>
                    <span className="cr-text-sm cr-text-gray-500">
                      {formatSynced(option.comparison.multigate_synced_ts)}
                    </span>
                    <Button
                      variant={confirmReferenceGate === option.id ? 'danger' : 'ghost'}
                      size="sm"
                      normalCase
                      onClick={() => onSetReference(option.id)}
                      disabled={referenceDisabled}
                    >
                      {confirmReferenceGate === option.id ? 'Confirm' : 'Set as Reference'}
                    </Button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The two-part sync progress: indeterminate while rows are copied, then filling as runs reprocess. */
function SyncProgressBar({ view }: { view: SyncProgressView }): ReactElement {
  const percent = view.fraction == null ? null : Math.round(view.fraction * 100);
  const barClass = percent == null
    ? 'cr-h-full cr-w-full cr-animate-pulse cr-rounded-full cr-bg-blue-300'
    : 'cr-h-full cr-rounded-full cr-bg-blue-600 cr-transition-all cr-duration-500';
  return (
    <div className="cr-max-w-xl cr-space-y-1" aria-live="polite">
      <div className="cr-flex cr-justify-between cr-gap-3 cr-text-sm cr-text-blue-900">
        <span>{view.label}</span>
        {percent != null && view.active && <span>{percent}%</span>}
      </div>
      <div
        className="cr-h-2 cr-w-full cr-overflow-hidden cr-rounded-full cr-bg-gray-200"
        role="progressbar"
        aria-label={view.label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
      >
        <div className={barClass} style={percent == null ? undefined : { width: `${percent}%` }} />
      </div>
      {view.detail && <p className="cr-text-sm cr-text-gray-600">{view.detail}</p>}
    </div>
  );
}
